import React, { useState } from 'react';
import { Calendar, User, Mail, Phone, MapPin, Clock, Eye, FileText } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import LoadingSpinner from '../components/LoadingSpinner';
import ErrorMessage from '../components/ErrorMessage';
import { useAPI } from '../hooks/useAPI';
import { bookingsAPI, contactsAPI } from '../services/api';

const AdminPage = () => {
  const [activeTab, setActiveTab] = useState('bookings');

  // API calls
  const { 
    data: bookings, 
    loading: bookingsLoading, 
    error: bookingsError,
    refetch: refetchBookings 
  } = useAPI(() => bookingsAPI.getAll());

  const { 
    data: contacts, 
    loading: contactsLoading, 
    error: contactsError,
    refetch: refetchContacts 
  } = useAPI(() => contactsAPI.getAll());

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('fr-FR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const BookingCard = ({ booking }) => (
    <Card className="mb-4">
      <CardHeader>
        <div className="flex justify-between items-start">
          <CardTitle className="flex items-center space-x-2">
            <Calendar className="w-5 h-5 text-blue-600" />
            <span>Réservation - {booking.date} à {booking.time}</span>
          </CardTitle>
          <Badge className="bg-green-100 text-green-800">
            Nouvelle
          </Badge>
        </div>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <h4 className="font-semibold text-gray-900 mb-2">Client</h4>
            <div className="space-y-1 text-sm">
              <div className="flex items-center space-x-2">
                <User className="w-4 h-4 text-gray-400" />
                <span>{booking.client?.firstName} {booking.client?.lastName}</span>
              </div>
              <div className="flex items-center space-x-2">
                <Mail className="w-4 h-4 text-gray-400" />
                <span>{booking.client?.email}</span>
              </div>
              <div className="flex items-center space-x-2">
                <Phone className="w-4 h-4 text-gray-400" />
                <span>{booking.client?.phone}</span>
              </div>
            </div>
          </div>
          <div>
            <h4 className="font-semibold text-gray-900 mb-2">Détails</h4>
            <div className="space-y-1 text-sm">
              <div><strong>Service:</strong> {booking.serviceId}</div>
              <div><strong>Véhicule:</strong> {booking.vehicle?.brand} {booking.vehicle?.model} {booking.vehicle?.year}</div>
              {booking.notes && (
                <div><strong>Notes:</strong> {booking.notes}</div>
              )}
            </div>
          </div>
        </div>
        <div className="mt-4 pt-4 border-t border-gray-200 text-xs text-gray-500">
          Reçu le {formatDate(booking.createdAt)}
        </div>
      </CardContent>
    </Card>
  );

  const ContactCard = ({ contact }) => (
    <Card className="mb-4">
      <CardHeader>
        <CardTitle className="flex items-center space-x-2">
          <FileText className="w-5 h-5 text-orange-600" />
          <span>{contact.subject || 'Message de contact'}</span>
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <h4 className="font-semibold text-gray-900 mb-2">Contact</h4>
            <div className="space-y-1 text-sm">
              <div className="flex items-center space-x-2">
                <User className="w-4 h-4 text-gray-400" />
                <span>{contact.firstName} {contact.lastName}</span>
              </div>
              <div className="flex items-center space-x-2">
                <Mail className="w-4 h-4 text-gray-400" />
                <span>{contact.email}</span>
              </div>
              {contact.phone && (
                <div className="flex items-center space-x-2">
                  <Phone className="w-4 h-4 text-gray-400" />
                  <span>{contact.phone}</span>
                </div>
              )}
            </div>
          </div>
          <div>
            <h4 className="font-semibold text-gray-900 mb-2">Message</h4>
            <p className="text-sm text-gray-600">{contact.message}</p>
          </div>
        </div>
        <div className="mt-4 pt-4 border-t border-gray-200 text-xs text-gray-500">
          Reçu le {formatDate(contact.createdAt)}
        </div>
      </CardContent>
    </Card>
  );

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="py-6">
            <h1 className="text-3xl font-bold text-gray-900">Administration AutoSpa 77</h1>
            <p className="mt-2 text-gray-600">Gestion des réservations et messages de contact</p>
          </div>
          
          {/* Tabs */}
          <div className="border-b border-gray-200">
            <nav className="-mb-px flex space-x-8">
              <button
                onClick={() => setActiveTab('bookings')}
                className={`py-2 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'bookings'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <Calendar className="w-4 h-4 inline mr-2" />
                Réservations ({bookings?.length || 0})
              </button>
              <button
                onClick={() => setActiveTab('contacts')}
                className={`py-2 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'contacts'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <FileText className="w-4 h-4 inline mr-2" />
                Messages ({contacts?.length || 0})
              </button>
            </nav>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {activeTab === 'bookings' && (
          <div>
            {bookingsLoading ? (
              <LoadingSpinner />
            ) : bookingsError ? (
              <ErrorMessage error={bookingsError} onRetry={refetchBookings} />
            ) : bookings && bookings.length > 0 ? (
              <div>
                <div className="flex justify-between items-center mb-6">
                  <h2 className="text-xl font-semibold text-gray-900">
                    Réservations reçues ({bookings.length})
                  </h2>
                  <Button onClick={refetchBookings} variant="outline">
                    Actualiser
                  </Button>
                </div>
                {bookings.map((booking) => (
                  <BookingCard key={booking.id} booking={booking} />
                ))}
              </div>
            ) : (
              <div className="text-center py-12">
                <Calendar className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">Aucune réservation</h3>
                <p className="text-gray-600">Les nouvelles réservations apparaîtront ici.</p>
              </div>
            )}
          </div>
        )}

        {activeTab === 'contacts' && (
          <div>
            {contactsLoading ? (
              <LoadingSpinner />
            ) : contactsError ? (
              <ErrorMessage error={contactsError} onRetry={refetchContacts} />
            ) : contacts && contacts.length > 0 ? (
              <div>
                <div className="flex justify-between items-center mb-6">
                  <h2 className="text-xl font-semibold text-gray-900">
                    Messages reçus ({contacts.length})
                  </h2>
                  <Button onClick={refetchContacts} variant="outline">
                    Actualiser
                  </Button>
                </div>
                {contacts.map((contact) => (
                  <ContactCard key={contact.id} contact={contact} />
                ))}
              </div>
            ) : (
              <div className="text-center py-12">
                <FileText className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">Aucun message</h3>
                <p className="text-gray-600">Les nouveaux messages de contact apparaîtront ici.</p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminPage;